// src/hooks/useAddressAutocomplete.js
// Calls GET /api/locations/autocomplete/?q=<query>
// Returns { suggestions: [{ description, lat, lng, place_id }], ... }

import { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../utils/axiosConfig';

/**
 * @param {number} debounceMs  - how long to wait after last keystroke before fetching (default 350ms)
 * @param {number} minLength   - minimum query length before fetching (default 2)
 */
const useAddressAutocomplete = (debounceMs = 350, minLength = 2) => {
  const [query, setQuery]             = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const timerRef                      = useRef(null);
  const controllerRef                 = useRef(null);

  const fetchSuggestions = useCallback(async (q) => {
    // Cancel any in-flight request
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = new AbortController();

    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('locations/autocomplete/', {
        params: { q },
        signal: controllerRef.current.signal,
      });
      setSuggestions(res.data?.suggestions || []);
    } catch (err) {
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        setError('Could not load suggestions');
        setSuggestions([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!query || query.length < minLength) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    timerRef.current = setTimeout(() => fetchSuggestions(query), debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, debounceMs, minLength, fetchSuggestions]);

  const clear = useCallback(() => {
    setQuery('');
    setSuggestions([]);
    setError(null);
  }, []);

  return { query, setQuery, suggestions, loading, error, clear };
};

export default useAddressAutocomplete;
